import { SalaryHistoryView } from '@hr/statisticsview/salaryhistory/SalaryHistoryView.tsx'
import { createBrowserRouter, Navigate } from 'react-router'
import { ActivateProcountor } from '../activate-procountor.jsx'
import { PremiumAd } from '../common/alertpremium.jsx'
import { ComparePersons } from '../common/checkemployees.jsx'

// Sidebars
import {
	DashboardSidebar,
	MgmtSidebar,
	NotifSidebar,
	OrganizationSidebar,
	PersonalSidebar,
	SalarySidebar,
	SettingsSidebar,
	StatisticsSidebar,
} from '../common/sidebar.jsx'

// Views
import DashboardView from '../dashboardview/index.jsx'
import { EpalkatFrame, Grid, GridFrame } from '../gridframe.jsx'
import { AbsenceManagement } from '../mgmtview/absences/index.jsx'
import CalendarTabsView from '../mgmtview/calendar/calendartabs.jsx'
import CompetencesView from '../mgmtview/competences/index.jsx'
import ContractView from '../mgmtview/contracts/index.jsx'
import DiscussionView from '../mgmtview/discussions/index.jsx'
import EmploymentCertificatesView from '../mgmtview/employmentcertificates/index.jsx'
import EquipmentView from '../mgmtview/equipment/index.jsx'
import { PersonalCalendarTabs } from '../mgmtview/personalprofile/personalCalendarTabs.jsx'
import PersonalDashboardView from '../mgmtview/personalprofile/personaldashboard.jsx'
import PersonnelView from '../mgmtview/personnel-view/index.jsx'
import { WorkhoursManagement } from '../mgmtview/workhours/index.jsx'
import { NotificationView } from '../notifview/index.jsx'
import { PersonHierarchy } from '../organizationview/hierarchy.jsx'
import { OrganizationIntegrations } from '../organizationview/integrations/index.jsx'
import OrganizationDetails from '../organizationview/organizationdetails.jsx'
import OrganizationDocuments from '../organizationview/organizationdocuments/index.jsx'
import { CalendarSettings } from '../settingsview/calendar-settings/CalendarSettings.jsx'
import UserManagement from '../settingsview/usermanagement.jsx'
import { AbsenceView } from '../statisticsview/absences.jsx'
import AnniversaryView from '../statisticsview/anniversaries.jsx'
import { EmploymentHistory } from '../statisticsview/employmenthistory.jsx'
import { PekkasetView } from '../statisticsview/pekkaset.jsx'
import { PersonnelStatements } from '../statisticsview/statements/index.jsx'
import { VacancyView } from '../statisticsview/vacancy/VacancyView.tsx'
import { WorkhoursView } from '../statisticsview/workhours.jsx'
import { AuthenticatedLayout } from './AuthenticatedLayout.jsx'
import { Home } from './Home.jsx'
import { ProtectedRoute } from './ProtectedRoute.jsx'
import { RootLayout } from './RootLayout.jsx'

// Helper to create route with metadata
const createRoute = (path, element, metadata = {}) => ({
	path,
	element,
	handle: metadata,
})

export const router = createBrowserRouter([
	{
		path: '/',
		element: <RootLayout />,
		children: [
			// Unauthenticated routes
			{
				index: true, // exactly "/"
				//path: "login",
				element: <Home />, // decides login vs ProtectedRoute
			},
			{
				path: 'ag-grid',
				element: <Grid />,
			},
			{
				path: 'grid-frame',
				element: <GridFrame />,
			},
			{
				path: 'activate-procountor',
				element: <ActivateProcountor />,
			},
			{
				path: 'reset-password',
				element: <Home />,
			},
			// Authenticated routes
			{
				element: <ProtectedRoute />,
				children: [
					{
						element: <AuthenticatedLayout />,
						children: [
							// Dashboard
							createRoute('dashboard', <DashboardView />, {
								sidebar: DashboardSidebar,
								equipment: true,
								noBgImage: true,
							}),
							createRoute('check-employees', <ComparePersons />, {
								sidebar: null,
							}),
							createRoute('premium', <PremiumAd />, {
								sidebar: null,
							}),

							// Management
							createRoute('mgmt', <PersonnelView />, {
								sidebar: MgmtSidebar,
							}),
							createRoute('mgmt/persons/:personNumber?', <PersonnelView />, {
								sidebar: MgmtSidebar,
							}),
							createRoute('mgmt/calendar', <CalendarTabsView />, {
								sidebar: MgmtSidebar,
							}),
							createRoute('mgmt/competences', <CompetencesView />, {
								sidebar: MgmtSidebar,
								premium: true,
							}),
							createRoute('mgmt/contracts/:contractIdentifier?', <ContractView />, {
								sidebar: MgmtSidebar,
							}),
							createRoute('mgmt/employmentcertificates', <EmploymentCertificatesView />, {
								sidebar: MgmtSidebar,
							}),
							createRoute('mgmt/discussions', <DiscussionView />, {
								sidebar: MgmtSidebar,
								premium: true,
							}),
							createRoute('mgmt/equipment', <EquipmentView />, {
								sidebar: MgmtSidebar,
								premium: true,
								equipment: true,
							}),

							// Personal
							createRoute('personal/dashboard', <PersonalDashboardView />, {
								sidebar: PersonalSidebar,
								personal: true,
								noBgImage: true,
							}),
							createRoute('personal/calendar', <PersonalCalendarTabs />, {
								sidebar: PersonalSidebar,
								personal: true,
							}),
							createRoute('personal/competences', <CompetencesView isPersonal />, {
								sidebar: PersonalSidebar,
								personal: true,
							}),
							createRoute('personal/contracts', <ContractView isPersonal />, {
								sidebar: PersonalSidebar,
								personal: true,
							}),
							createRoute('personal/employmentcertificates', <EmploymentCertificatesView isPersonal />, {
								sidebar: PersonalSidebar,
								personal: true,
							}),
							createRoute('personal/discussions', <DiscussionView />, {
								sidebar: PersonalSidebar,
								personal: true,
							}),
							createRoute('personal/equipment', <EquipmentView isPersonal />, {
								sidebar: PersonalSidebar,
								personal: true,
							}),

							// Statistics
							createRoute('stats', <AnniversaryView />, {
								sidebar: StatisticsSidebar,
							}),
							createRoute('stats/anniversaries', <AnniversaryView />, {
								sidebar: StatisticsSidebar,
							}),
							createRoute('stats/vacancy', <VacancyView />, {
								sidebar: StatisticsSidebar,
							}),
							createRoute('stats/workhours', <WorkhoursView />, {
								sidebar: StatisticsSidebar,
							}),
							createRoute('stats/salaryabsences', <AbsenceView />, {
								sidebar: StatisticsSidebar,
							}),
							createRoute('stats/pekkaset', <PekkasetView />, {
								sidebar: StatisticsSidebar,
							}),
							createRoute('stats/statements', <PersonnelStatements />, {
								sidebar: StatisticsSidebar,
							}),
							createRoute('stats/employmenthistory', <EmploymentHistory />, {
								sidebar: StatisticsSidebar,
							}),
							createRoute('stats/salaryhistory', <SalaryHistoryView />, {
								sidebar: StatisticsSidebar,
							}),

							// Organization
							createRoute('organization', <OrganizationDetails />, {
								sidebar: OrganizationSidebar,
							}),
							createRoute('organization/details', <OrganizationDetails />, {
								sidebar: OrganizationSidebar,
							}),
							createRoute('organization/documents', <OrganizationDocuments />, {
								sidebar: OrganizationSidebar,
							}),
							createRoute('organization/integrations', <OrganizationIntegrations />, {
								sidebar: OrganizationSidebar,
								noBgImage: true,
							}),
							createRoute('organization/hierarchy', <PersonHierarchy />, {
								sidebar: OrganizationSidebar,
							}),

							// Settings
							createRoute('settings', <UserManagement />, {
								sidebar: SettingsSidebar,
							}),
							createRoute('settings/users', <UserManagement />, {
								sidebar: SettingsSidebar,
							}),

							createRoute('settings/calendar', <CalendarSettings />, {
								sidebar: SettingsSidebar,
							}),

							// Salary (epalkat)
							createRoute('epalkat/one-time-payment', <EpalkatFrame />, {
								sidebar: SalarySidebar,
							}),
							createRoute('epalkat/paymentapproval', <EpalkatFrame path="maksuhyvframe" />, {
								sidebar: SalarySidebar,
							}),
							createRoute('epalkat/salarytemplate', <EpalkatFrame path="salarySheet" />, {
								sidebar: SalarySidebar,
							}),
							createRoute('epalkat/absences', <AbsenceManagement />, {
								sidebar: SalarySidebar,
							}),
							createRoute('epalkat/workhours', <WorkhoursManagement />, {
								sidebar: SalarySidebar,
							}),

							// Notifications
							createRoute('notif', <NotificationView />, {
								sidebar: NotifSidebar,
								premium: true,
							}),
						],
					},
				],
			},
			// Catch-all redirect
			{
				path: '*',
				element: <Navigate to="/" replace />,
			},
		],
	},
])
